/**
 * PATH: apps/qa/src/pages/report-editor/SectionSidebar.tsx
 * PURPOSE: Section navigation sidebar for the report editor
 */

import { cn } from '@/lib/utils'
import { getSectionLabel, SECTION_ICONS } from './constants'
import { FileText, GripVertical } from 'lucide-react'

interface SectionSidebarProps {
  sectionOrder: string[]
  activeSection: string
  draggedSection: string | null
  draft: { sections: Array<{ section_type: string; content?: string; is_auto_generated?: boolean; word_count: number }> } | undefined
  onSelectSection: (sectionType: string) => void
  onDragStart: (sectionType: string) => void
  onDragOver: (e: React.DragEvent, targetSection: string) => void
  onDragEnd: () => void
}

export function SectionSidebar({
  sectionOrder, activeSection, draggedSection, draft,
  onSelectSection, onDragStart, onDragOver, onDragEnd,
}: SectionSidebarProps) {
  const getSectionStatus = (sectionType: string): 'empty' | 'auto' | 'edited' => {
    const section = draft?.sections.find((s) => s.section_type === sectionType)
    if (!section || !section.content || section.content.trim().length === 0) return 'empty'
    if (section.is_auto_generated) return 'auto'
    return 'edited'
  }

  return (
    <div className="hidden w-56 shrink-0 border-r border-border bg-card/50 md:flex md:flex-col">
      <nav className="flex-1 overflow-y-auto p-2 space-y-1">
        {sectionOrder.map((sectionType) => {
          const sectionStatus = getSectionStatus(sectionType)
          const Icon = SECTION_ICONS[sectionType] || FileText
          const section = draft?.sections.find((s) => s.section_type === sectionType)

          return (
            <button
              key={sectionType}
              draggable
              onDragStart={() => onDragStart(sectionType)}
              onDragOver={(e) => onDragOver(e, sectionType)}
              onDragEnd={onDragEnd}
              onClick={() => onSelectSection(sectionType)}
              className={cn(
                'w-full flex items-center gap-2 px-2 py-2.5 rounded-lg text-sm transition-all text-left group',
                activeSection === sectionType
                  ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                draggedSection === sectionType && 'opacity-50 ring-2 ring-primary'
              )}
            >
              <GripVertical className="w-3.5 h-3.5 shrink-0 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing" />
              <div className={cn(
                'w-2 h-2 rounded-full shrink-0 transition-colors',
                sectionStatus === 'empty' && 'bg-muted-foreground/30',
                sectionStatus === 'auto' && 'bg-amber-500',
                sectionStatus === 'edited' && 'bg-emerald-500'
              )} />
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="block truncate">{getSectionLabel(sectionType)}</span>
                {section && section.word_count > 0 && (
                  <span className="text-[10px] text-muted-foreground">{section.word_count} words</span>
                )}
              </div>
            </button>
          )
        })}
      </nav>
      <div className="shrink-0 p-3 border-t border-border">
        <div className="space-y-1.5 text-[10px]">
          <div className="flex items-center gap-2 text-muted-foreground"><div className="w-2 h-2 rounded-full bg-muted-foreground/30" /><span>Empty</span></div>
          <div className="flex items-center gap-2 text-muted-foreground"><div className="w-2 h-2 rounded-full bg-amber-500" /><span>Auto-populated</span></div>
          <div className="flex items-center gap-2 text-muted-foreground"><div className="w-2 h-2 rounded-full bg-emerald-500" /><span>User Edited</span></div>
        </div>
      </div>
    </div>
  )
}
